import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { track } from "../lib/analytics";
import ProductCard from "../components/ProductCard";
import { Filter, ChevronDown } from "lucide-react";

const AGES = ["0-6M", "6-12M", "1-2Y", "2-3Y", "3-4Y"];
const SIZES = ["0-6M", "6-12M", "1-2Y", "2-3Y", "3-4Y", "S", "M", "One Size"];

export default function Shop({ categorySlug }) {
    const { slug } = useParams();
    const [params, setParams] = useSearchParams();
    const activeCat = slug || categorySlug || "all";
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const [age, setAge] = useState(params.get("age") || "");
    const [size, setSize] = useState(params.get("size") || "");
    const [color, setColor] = useState(params.get("color") || "");
    const [sort, setSort] = useState(params.get("sort") || "newest");
    const [priceRange, setPriceRange] = useState(params.get("price") || "");

    useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);

    useEffect(() => {
        setLoading(true);
        const p = {};
        if (activeCat !== "all") p.category = activeCat;
        if (age) p.age = age;
        if (size) p.size = size;
        if (color) p.color = color;
        if (priceRange === "under1000") p.max_price = 999;
        if (priceRange === "1000-2000") { p.min_price = 1000; p.max_price = 2000; }
        if (priceRange === "above2000") p.min_price = 2000;
        if (sort) p.sort = sort;
        api.get("/products", { params: p }).then(({ data }) => {
            setProducts(data);
            track("view_item_list", { list_name: `category_${activeCat}`, items: data.slice(0,12).map((pp,i)=>({item_id:pp.id, item_name:pp.name, item_category:pp.category, price:pp.price, index:i})) });
        }).finally(() => setLoading(false));
    }, [activeCat, age, size, color, sort, priceRange]);

    const title = useMemo(() => {
        if (activeCat === "all") return "All Products";
        const c = categories.find((cc) => cc.slug === activeCat);
        return c?.name || "Shop";
    }, [activeCat, categories]);

    useEffect(() => { document.title = `${title} | Loomara`; }, [title]);

    const updateParam = (key, value, set) => {
        set(value);
        const np = new URLSearchParams(params);
        if (value) np.set(key, value); else np.delete(key);
        setParams(np, { replace: true });
    };

    const colorsAvailable = Array.from(new Set(products.flatMap((p) => p.colors || []))).slice(0, 10);

    return (
        <div data-testid="shop-page">
            {/* Banner */}
            <section className="bg-ivory-100 py-12 border-b border-sage-300/20">
                <div className="container-boutique">
                    <p className="eyebrow">Loomara Boutique</p>
                    <h1 className="font-serif text-4xl sm:text-5xl text-ink-900 mt-3">{title}</h1>
                    <p className="text-ink-500 mt-3 max-w-xl">Thoughtfully curated styles for soft beginnings.</p>
                </div>
            </section>

            <section className="container-boutique py-12">
                {/* Filters bar */}
                <div className="flex flex-wrap items-center gap-3 mb-8">
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-sage-700">
                        <Filter size={14} strokeWidth={1.5} /> Filters
                    </div>

                    <FilterSelect label="Age" value={age} onChange={(v)=>updateParam("age", v, setAge)} options={AGES} testid="filter-age" />
                    <FilterSelect label="Size" value={size} onChange={(v)=>updateParam("size", v, setSize)} options={SIZES} testid="filter-size" />
                    <FilterSelect label="Color" value={color} onChange={(v)=>updateParam("color", v, setColor)} options={colorsAvailable} testid="filter-color" />
                    <FilterSelect
                        label="Price"
                        value={priceRange}
                        onChange={(v)=>updateParam("price", v, setPriceRange)}
                        options={[
                            { value: "under1000", label: "Under ₹1000" },
                            { value: "1000-2000", label: "₹1000 — ₹2000" },
                            { value: "above2000", label: "Above ₹2000" },
                        ]}
                        testid="filter-price"
                    />

                    <div className="ml-auto">
                        <FilterSelect
                            label="Sort"
                            value={sort}
                            onChange={(v)=>updateParam("sort", v, setSort)}
                            options={[
                                { value: "newest", label: "Newest" },
                                { value: "price_asc", label: "Price: Low to High" },
                                { value: "price_desc", label: "Price: High to Low" },
                                { value: "popularity", label: "Popularity" },
                            ]}
                            testid="sort-products"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[1,2,3,4].map((i) => (
                            <div key={i} className="animate-pulse">
                                <div className="aspect-[4/5] rounded-2xl bg-ivory-100" />
                                <div className="h-4 bg-ivory-100 mt-4 rounded w-2/3" />
                                <div className="h-3 bg-ivory-100 mt-2 rounded w-1/3" />
                            </div>
                        ))}
                    </div>
                ) : products.length === 0 ? (
                    <div className="text-center py-20 text-ink-500" data-testid="empty-state">
                        <p className="font-serif text-2xl text-ink-900">Nothing matches your filters yet</p>
                        <p className="mt-2 text-sm">Try adjusting filters above — new pieces arrive every season.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="products-grid">
                        {products.map((p, i) => (<ProductCard key={p.id} product={p} index={i} listName={`category_${activeCat}`} />))}
                    </div>
                )}
            </section>
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    const normalized = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
    return (
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="appearance-none bg-white border border-sage-300/40 rounded-full pl-4 pr-9 py-2 text-xs uppercase tracking-[0.18em] text-ink-900 focus:outline-none focus:border-sage-500 cursor-pointer"
                data-testid={testid}
            >
                <option value="">{label}</option>
                {normalized.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-sage-600 pointer-events-none" />
        </div>
    );
}
